import React, { Suspense, useState, useEffect } from "react";
import "./App.css";
import "bootstrap/dist/css/bootstrap.min.css";
import { Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import useSectionHook from "./hooks/SectionHook";
import Genre from "./components/Genre/Genre";
import ActivateGuard from "./components/ActivateGuard/ActivateGuard";
import ThankYou from "./components/ThankYou/ThankYou";
import Auth from "./services/auth";
import HttpApis from "./services/HttpApis";
import { ConfigProvider, useConfig } from "./ConfigContext";
import { useTenant } from "./TenantContext";
import { applyTheme } from "./theme/applyTheme"; // <-- adjust path to where color.js actually lives
import ProfileScreen from "./components/ProfileScreen/ProfileScreen";
import CreateProfile from "./components/ProfileScreen/CreateProfile";
import EditProfile from "./components/ProfileScreen/EditProfile";
import UpdateProfile from "./components/ProfileScreen/UpdateProfile";
import { logout } from "./redux/slices/authSlice";
import ForgotPassword from "./components/ForgotPassword/ForgotPassword";
import ChangePassword from "./components/ChangePassowrd/ChangePassword";
import Loader from "./components/loader/Loader";
import ResetPassword from "./components/ForgotPassword/ResetPassword";
import NewPassword from "./components/ForgotPassword/NewPassword";
import "./components/Footer/legacyGlobals.css";
import LegalPage from "./components/Legal/LegalPage";
import { LEGAL_DOCS } from "./components/Legal/legalLinks";
import HelpCenter from "./components/HelpCenter/HelpCenter";
import ContactSupport from "./components/HelpCenter/ContactSupport";
import VerifyEmail from "./components/VerifyEmail/VerifyEmail";
import Unsubscribe from "./components/Unsubscribe/Unsubscribe";
import ThankYouPage from "./components/ThankyouRent/ThankyouPage";
import CastAndCrew from "./components/CastAndCrew/CastAndCrew";
import TenantNotFound from "./components/TenantNotFound/TenantNotFound";
const Layout = React.lazy(() => import("./components/Layout/layout"));
const DetailsPage = React.lazy(() => import("./components/Details/details"));
const Home_page = React.lazy(() => import("./components/Home_page/home"));
const Login_page = React.lazy(() => import("./pages/Login/login_page"));
const ActivatePage = React.lazy(() => import("./pages/Activate/ActivatePage"));
const Settings = React.lazy(() => import("./components/Settings/settings"));
const VideoPlayer = React.lazy(() => import("./components/Player/player"));
const FilterPage = React.lazy(() => import("./components/Filter/FilterPage"));
const AccountPage = React.lazy(() => import("./components/Account/AccountPage"));
const Subscription = React.lazy(() => import("./components/Account/Subscription"));
const AccountLink = React.lazy(() => import("./components/AccountLink/AccountLink"));
const Devices = React.lazy(() => import("./components/Devices/Devices"));
const CreatorDashboard = React.lazy(() => import("./components/Creator/CreatorDashboard"));
const CreatorChannel = React.lazy(() => import("./components/Creator/CreatorChannel"));
const CreatorsGrid = React.lazy(() => import("./components/Creator/CreatorsGrid"));
const SeeAll = React.lazy(() => import("./components/SeeAll/SeeAll"));
const ContactUs = React.lazy(() => import("./components/Footer/ContactUs"));
const Footer = React.lazy(() => import("./components/Footer/Footer"));
const LiveTV = React.lazy(() => import("./components/LiveTV/LiveTV"));
const LiveChannelPlayer = React.lazy(() => import("./components/LiveTV/LiveChannelPlayer"));
const MicroDramaPlayer = React.lazy(() => import("./components/MicroDrama/MicroDramaPlayer"));

const AppRoutes = () => {
  const { listData, selectedSectionID, sectionData, handleSectionClick, error } =
    useSectionHook();
  const navigate = useNavigate();
  const location = useLocation();
  const config = useConfig();
  const dispatch = useDispatch();
  const token = useSelector((state) => state.authReducer?.token);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const { tenant, loading: tenantLoading, error: tenantError, isMultiTenant } = useTenant();

  useEffect(() => {
    if (tenant?.name) {
      document.title = tenant.name;
      const descriptionTag = document.querySelector('meta[name="description"]');
      if (descriptionTag) {
        descriptionTag.setAttribute(
          "content",
          `Watch movies and shows on ${tenant.name} anytime, anywhere.`
        );
      }
    }
  }, [tenant?.name]);

  const handleDetails = (slug, type) => {
      navigate(`/details/${type}/${slug}`);

  };

  // Apply brand theme color (per-tenant from config, env var, or default)
  // to the global CSS variables as soon as config is available/changes.
  useEffect(() => {
    applyTheme(config);
  }, [config]);

  useEffect(() => {
    if (config?.enable_login === false) {
      const deviceId = localStorage.getItem("deviceId");
      localStorage.clear();
      if (deviceId) localStorage.setItem("deviceId", deviceId);
      dispatch(logout());

      const loginOnlyPaths = ["/profile", "/my-list", "/accountPage", "/subscribe"];
      if (loginOnlyPaths.includes(location.pathname)) {
        navigate("/", { replace: true });
      }
    }
  }, [config, location.pathname, navigate, dispatch]);

  // Referral capture: handles BOTH forms of a creator referral link —
  //   /ref/<code>   (path form, what the backend builds)
  //   ?ref=<code>   (query form)
  // Records the click, stashes the code + anonymous visitor token for signup
  // attribution, then (for the /ref/ path) redirects into the app.
  useEffect(() => {
    const pathMatch = location.pathname.match(/^\/ref\/([^/?#]+)/);
    const queryRef = new URLSearchParams(location.search).get("ref");
    const ref = pathMatch ? decodeURIComponent(pathMatch[1]) : queryRef;
    if (!ref) return;

    localStorage.setItem("ff_ref_code", ref);
    const existingToken = localStorage.getItem("ff_ref_visitor_token") || undefined;
    HttpApis.trackReferralClick({ code: ref, visitor_token: existingToken })
      .then((resp) => {
        const token = resp?.data?.data?.visitor_token;
        if (token) localStorage.setItem("ff_ref_visitor_token", token);
      })
      .catch(() => {});

    // If we arrived via the /ref/<code> path, send the visitor into the app.
    // A referral link should default to SIGN UP for logged-out visitors, so we
    // pass the ref code + a signup flag to the auth screen (which then opens the
    // sign-up view). Logged-in users just go home.
    if (pathMatch) {
      const loggedIn = !!localStorage.getItem("authData");
      navigate(
        loggedIn ? "/" : `/login?signup=1&ref=${encodeURIComponent(ref)}`,
        { replace: true }
      );
    }
  }, [location.pathname, location.search, navigate]);

  useEffect(() => {
    if (config?.enable_login !== false && token) {
      const profileSelected = localStorage.getItem('profileSelected');
      if (location.pathname === "/" && profileSelected) {
        navigate("/profile", { replace: true });
      }
    }
  setCheckingAuth(false);
}, [token, location.pathname, navigate, config]);


  useEffect(() => {
    if (config?.enable_login === false || !token) return;
    Auth.checkAndRefreshOnLoad();
    Auth.startAutoRefresh();

    return () => {
      Auth.stopAutoRefresh();
    };
  }, [config?.enable_login, token]);


  if (tenantLoading) return <Loader />;
  if (isMultiTenant && !tenant) {
    return <TenantNotFound message={tenantError} host={typeof window !== 'undefined' ? window.location.hostname : undefined} />;
  }
  if (checkingAuth) return null;

  return (
    <Routes>
      {(!config || config.enable_login !== false) && (
        <Route path="/login" element={<Login_page />} />
      )}
       <Route path="/forgotPassword" element={<ForgotPassword />} />
         <Route path="/changePassword" element={<ChangePassword />} />
         <Route path="/resetPassword" element={<ResetPassword />} />
          <Route path="/newPassword" element={<NewPassword />} />
          {/* Signup email verification — reached right after register */}
          <Route path="/verifyEmail" element={<VerifyEmail />} />
          {/* Marketing email "Unsubscribe" link — public, token carries the credential */}
          <Route path="/unsubscribe" element={<Unsubscribe />} />

      {(!config || config.enable_login !== false) && (
        <>
          <Route path="/profile" element={<ProfileScreen />} />
          <Route path="/CreateProfile" element={<CreateProfile />} />
          <Route path="/editProfile" element={<EditProfile />} />
          <Route path="/updateProfile" element={<UpdateProfile />} />
        </>
      )}

      {(!config || config.enable_qrlogin !== false) && (
        <>
          <Route
            path="/activateCode"
            element={
              <ActivateGuard>
                <Login_page />
              </ActivateGuard>
            }
          />
          <Route path="/activate" element={<ActivatePage />} />
        </>
      )}
      {/* Legal pages: content and publish state come from the CMS. Old paths
          redirect so existing links keep working. */}
      {LEGAL_DOCS.map((doc) => (
        <Route key={doc.type} path={doc.path} element={<LegalPage type={doc.type} />} />
      ))}
      {LEGAL_DOCS.map((doc) => (
        <Route key={`${doc.type}-legacy`} path={doc.legacyPath} element={<Navigate to={doc.path} replace />} />
      ))}
      <Route path="/Contact-Us" element={<ContactUs />} />
      <Route path="/help-center" element={<HelpCenter />} />
      <Route path="/support" element={<ContactSupport />} />


      <Route path="/thank-you" element={<ThankYou />} />
      <Route path="/thankyou-buyrent" element={<ThankYouPage />} />

      {/* Standalone info pages — rendered without the main Layout/nav */}
      <Route path="/contact-us" element={<ContactUs />} />

      <Route
        path="/"

        element={
          <Layout
            selectedSectionID={selectedSectionID}
            listData={listData}
            handleSectionClick={handleSectionClick}
            error={error}
          />
        }
      >
        {(!config || config.enable_home !== false) && (
          <Route
            index
            element={
              <Home_page
                selectedSectionID={selectedSectionID}
                sectionData={sectionData}
                handleDetails={handleDetails}
              />
            }
          />
        )}
        
        <Route
          path="genre/:genre_id"
          element={<Genre handleDetails={handleDetails} />}
        />
        <Route
          path="details/:type/:slug"
          element={<DetailsPage handleDetails={handleDetails} />}
        />
        <Route path="settings" element={<Settings />} />
        <Route
          path="player/:type/:slug"
          element={<VideoPlayer />}
        />
        <Route
          path="search"
          element={<FilterPage handleDetails={handleDetails} />}
        />
        <Route path="see-all" element={<SeeAll />} />
        <Route
          path="accountPage"
          element={<AccountPage handleDetails={handleDetails} />}
        />
        {(!config || config.enable_subscription !== false) && (
          <Route
            path="subscribe"
            element={
              <>
                <Subscription />
                <Footer />
              </>
            }
          />
        )}
        <Route path="link" element={<AccountLink />} />
        <Route path="devices" element={<Devices />} />
        <Route path="/CastAndCrew/:id" element={<CastAndCrew />} />
        <Route path="/genre/:genre_id" element={<Genre />} />
        <Route path="creator-dashboard" element={<CreatorDashboard />} />
        <Route path="creator-channel/:id" element={<CreatorChannel />} />
        <Route path="creators" element={<CreatorsGrid />} />
        {/* Playo live channels: grid, guide, player */}
        <Route path="live-tv" element={<LiveTV />} />
        <Route path="live-tv/guide" element={<Navigate to="/live-tv" replace />} />
        <Route path="live-tv/watch/:slug" element={<LiveChannelPlayer />} />
        {/* Micro drama series: vertical swipe player */}
        <Route path="micro/:slug" element={<MicroDramaPlayer />} />

        <Route
          path="*"
          element={<Home_page handleDetails={handleDetails} />}
        />
      </Route>
    </Routes>
  );
};

const App = () => {
  useEffect(() => {
    const key = "deviceId";
    let deviceId = localStorage.getItem(key);
    if (!deviceId) {
      deviceId = uuidv4();
      localStorage.setItem(key, deviceId);
    }
  }, []);

  return (
    <ConfigProvider>
      <Suspense fallback={<Loader />}>
        <div style={{ background: "var(--color-bg, #191919)" }}>
          <AppRoutes />
        </div>
      </Suspense>
    </ConfigProvider>
  );
};

export default App;
